import { Box, Button, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { IWithClassName } from "../../../theme/default.theme";
import {
  IRootState,
  useAppSelector,
  useAppDispatch,
} from "../../../redux/store";
import { createSelector } from "@reduxjs/toolkit";
import Joi from "joi";
import { updateProfileThunk } from "../../../redux/thunks/user.thunk";
import { BusinessType } from "../../../enum/business";
import DashboardFrame from "../../../component/dashboard-frame";
import { getUserInfoThunk } from "../../../redux/thunks/user.thunk";
import { getCampaignThunk } from "../../../redux/thunks/campaign.thunk";

const selector = createSelector(
  (state: IRootState) => state.user,
  (user) => ({
    info: user.info,
    business: user.business.data?.data[0],
  }),
);

interface IError {
  name?: string;
  country?: string;
  businessName?: string;
}

const Profile = ({ className }: IWithClassName) => {
  const { info, business } = useAppSelector(selector);
  const dispatch = useAppDispatch();

  const [error, setError] = useState<IError>({});
  const [profileForm, setProfileForm] = useState<{
    name: string;
    country: string;
    businessName: string;
    email: string;
    status: string;
  }>({
    name: info.data?.name ?? "",
    email: "",
    status: "",
    country: "",
    businessName: "",
  });

  useEffect(() => {
    if (info) {
      setProfileForm({
        name: info.data?.name ?? "",
        email: info.data ? info.data.email.address : "",
        status: info.data ? info.data.status : "",
        country: business ? business.country : "",
        businessName: business ? business.name : "",
      });
    }
  }, [info, business]);

  const schema = Joi.object({
    businessName: Joi.string().required().min(3).messages({
      "string.min": "Company name should have a minimum length of {#limit}",
      "any.required": "Company name is a required field",
      "string.empty": "Company name name is not allowed to be empty",
    }),
    country: Joi.string().required().messages({
      "any.required": "Country is a required field",
    }),
    name: Joi.string().required().min(3).messages({
      "string.min": "First name should have a minimum length of {#limit}",
      "any.required": "First name is a required field",
      "string.empty": "First name is not allowed to be empty",
    }),
  });

  useEffect(() => {
    dispatch(getUserInfoThunk());
    dispatch(getCampaignThunk());
  }, [dispatch]);

  const onSubmit = () => {
    const { country, name, businessName } = profileForm;
    schema
      .validateAsync({ country, name, businessName })
      .then((value) => {
        dispatch(
          updateProfileThunk(business?.id ?? "", {
            country,
            companyName: businessName,
            name: name,
            type: BusinessType.Agency,
          }),
        );
      })
      .catch((err) => {
        setError((state) => ({
          [err.details[0].path[0]]: err.details[0].message,
        }));
      });
  };

  const onChange =
    (key: string) => (event: React.ChangeEvent<HTMLInputElement>) =>
      setProfileForm((form) => ({
        ...form,
        [key]: event.currentTarget.value,
      }));

  return (
    <DashboardFrame title="Profile">
      <Box className={className}>
        <TextField
          placeholder="Name"
          value={profileForm.name}
          error={!!error.name}
          helperText={error.name}
          onChange={onChange("name")}
        />
        <TextField placeholder="Email" value={profileForm.email} />
        <TextField
          placeholder="Company Name"
          value={profileForm.businessName}
          error={!!error.businessName}
          helperText={error.businessName}
          onChange={onChange("businessName")}
        />
        <TextField placeholder="Status" value={profileForm.status} />
        {/* <CountrySelector
          value={profileForm.country}
          onchange={(country) => setProfileForm((form) => ({ ...form, country }))}
          placeholder="Country"
        /> */}
        <Button variant="contained" onClick={onSubmit}>
          Update
        </Button>
      </Box>
    </DashboardFrame>
  );
};

export default Profile;
